<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useId } from 'vue';

type Item = { name: string; href: string; imgSrc: string };

defineProps<{ items?: Item[]; title: string; content?: string }>();

const id = useId();
const isOpen = ref(false);

const controlRef = ref<HTMLButtonElement | null>(null);
const teleportTarget = ref<Element | null>(null);

function openPopover() {
  isOpen.value = true;
}

function closePopover() {
  isOpen.value = false;
  controlRef.value?.focus();
}

function togglePopover() {
  isOpen.value ? closePopover() : openPopover();
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && isOpen.value)
    closePopover();
}

onMounted(() => {
  // getRootNode() returns the ShadowRoot when this component has been
  // compiled into a custom element, or `document` otherwise. Teleporting
  // there (instead of leaving the popover nested where it's declared)
  // makes it a top-level sibling within the *same* style scope, so the
  // component's scoped CSS / custom properties still apply, while
  // escaping any local stacking-context issues.
  // `document` itself can't be an append target, so fall back to <body>.
  const root = controlRef.value?.getRootNode();
  teleportTarget.value = (root === document ? document.body : root) as Element ?? document.body;
  document.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <button
    id="popover_control"
    ref="controlRef"
    type="button"
    :aria-expanded="isOpen"
    :aria-controls="`popover_${id}`"
    @click="togglePopover"
  >
    <slot name="button" />
  </button>

  <Teleport v-if="teleportTarget" :to="teleportTarget">
    <Transition name="popover-fade">
      <div v-if="isOpen" class="popover-backdrop" @click="closePopover" />
    </Transition>
    <Transition name="popover-fade">
      <div
        v-if="isOpen"
        :id="`popover_${id}`"
        class="popover-panel"
        role="dialog"
        aria-modal="true"
      >
        <h3 class="title">
          {{ title }}
        </h3>
        <div v-if="items && items.length" class="wrapper">
          <a
            v-for="item in items"
            :key="item.name"
            class="link"
            :href="item.href"
            target="_blank"
            rel="noopener"
          >
            <img width="32" height="32" :src="item.imgSrc" :alt="item.name">
            <p class="label">{{ item.name }}</p>
          </a>
        </div>
        <div v-if="!items && content" class="wrapper">
          <p class="content">
            {{ content }}
          </p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
#popover_control {
  display: flex;
  align-items: center;
  appearance: none;
  background-color: transparent;
  color: var(--yb-color-black);
  font-size: var(--yb-text-sm);
  border: none;
  padding-inline: 0px;
  cursor: pointer;

  &:hover {
    color: var(--yb-color-primary);
  }
}

.popover-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 98;
}

.popover-panel {
  position: fixed;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -45%);
  width: min(95vw, 230px);
  height: fit-content;
  border: none;
  border-radius: var(--yb-radius-sm);
  padding: 1.25rem;
  justify-items: center;
  background-color: white;
  box-shadow: rgba(0, 0, 0, 0.24) 0 3px 8px;
  font-family: inherit;
  font-size: var(--yb-text-base);
  z-index: 100;

  .title {
    font-size: 1rem;
    font-weight: 700;
    color: var(--yb-color-black);
    margin-bottom: var(--yb-radius-base);
    white-space: nowrap;
    margin-block-start: 0;
  }

  &:has(.content) {
    min-width: min(90vw, 540px);
    justify-items: start;
  }
}

.wrapper {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  width: 100%;
  gap: var(--yb-radius-base);
  font-weight: var(--yb-text-light);
  font-size: var(--yb-text-sm);

  .link {
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    gap: var(--yb-radius-base);
    background-color: var(--yb-color-white);
    width: 90%;
    margin-inline: auto;
    border-radius: var(--yb-radius-sm);
    border: 1px solid var(--yb-color-mediumgray);
    padding: var(--yb-radius-sm);
    cursor: pointer;

    &:hover {
      border: 1px solid var(--search-highlight-background-color);
      background-color: var(--search-highlight-background-color);
    }
  }

  .content {
    color: var(--yb-color-black);
    font-weight: 400;
    font-size: 1rem;
  }
}

.popover-fade-enter-active,
.popover-fade-leave-active {
  transition: opacity 0.15s ease;
}
.popover-fade-enter-from,
.popover-fade-leave-to {
  opacity: 0;
}
</style>
